import { Orientation3D, Position3D } from '@fixtuur/fine';
import { Context, Effect, Layer, Array, Record, pipe } from 'effect';
import { flow } from 'fp-ts/lib/function';
import { iso } from 'newtype-ts';
import { v4 as uuidv4 } from 'uuid';
import { Article } from '../aspect';
import * as AspectFurniture from '../aspect/furniture';
import * as Config from '../configuration/configuration';
import * as Particle from '../configuration/particle';
// provides ability to wrap, unwrap, and modify the newtype
const _ = iso();
// export functions from here
export const create = _.wrap;
// Furniture Property Getters
export const instanceId = flow(_.unwrap, x => x.instanceId);
export const configuration = flow(_.unwrap, x => x.configuration);
export const position = flow(_.unwrap, x => x.position);
export const rotation = flow(_.unwrap, x => x.rotation);
// Furniture Functions
export const applyFurnitureRotation = (rotation) => _.modify(f => ({ ...f, rotation: rotation }));
export const applyFurniturePosition = (position) => _.modify(f => ({ ...f, position }));
export class GenerationFailure {
    furniture;
    errors;
    _tag = 'Fixtuur/Furniture/GenerationFailure';
    constructor(furniture, errors) {
        this.furniture = furniture;
        this.errors = errors;
    }
}
export class SlotMismatchError {
    missingSlots;
    _tag = 'Fixtuur/Aspect/Furniture/SlotMismatchError';
    constructor(missingSlots) {
        this.missingSlots = missingSlots;
    }
}
export class FixtuurFurnitureService extends Context.Tag('@fixtuur/backpack/FixtuurFurnitureService')() {
}
export const BaseFixtuurFurnitureLayer = Layer.effect(FixtuurFurnitureService, Effect.gen(function* (_) {
    const particleService = yield* _(Particle.FixtuurConfigurationParticleService);
    const fromAspectFurniture = af => {
        const position = Position3D.fromTuple([0, 0, 0]);
        const rotation = Orientation3D.fromEuler([0, 0, 0]);
        const instanceId = uuidv4();
        const possibleChildren = Record.values(af.selections);
        const tenantId = AspectFurniture.tenant(af);
        const particles = Effect.iterate({
            _tag: 'GenerateParticlesInitialState',
            tenantId,
            aspectFurniture: af,
        }, {
            while: state => {
                if (state._tag === 'GenerateParticlesSuccess' ||
                    state._tag === 'GenerateParticlesFailure') {
                    return false;
                }
                return true;
            },
            body: state => pipe(state.aspectFurniture.selections, Record.values, Array.map(article => particleService.fromAspectArticle(article, {
                tenantId,
                handedness: af.handedness,
                possibleChildren,
            })), Effect.all, Effect.map(particles => {
                return {
                    _tag: 'GenerateParticlesSuccess',
                    particles,
                    errors: state._tag === 'GenerateParticlesIntermediate'
                        ? state.errors
                        : [],
                    aspectFurniture: state.aspectFurniture,
                };
            }), Effect.catchTag('Fixtuur/Aspect/Particle/AttachmentError', e => {
                const article = e.article;
                const furniture = state.aspectFurniture;
                const errors = state._tag === 'GenerateParticlesIntermediate'
                    ? state.errors.concat(e)
                    : [e];
                const checkedProductIds = errors.map(e => e.article.productId);
                const tenantId = state._tag === 'GenerateParticlesInitialState' ||
                    state._tag === 'GenerateParticlesIntermediate'
                    ? state.tenantId
                    : undefined;
                const articleId = article.articleId;
                const part = furniture.configurationSet.parts.find(p => p.productType === articleId);
                const productIds = part?.productIds.filter(id => !checkedProductIds.includes(id)) ?? [];
                if (productIds.length === 0) {
                    return Effect.succeed({
                        _tag: 'GenerateParticlesFailure',
                        aspectFurniture: furniture,
                        reasons: errors,
                    });
                }
                else {
                    // swap the selection in the aspectFurniture for a new Article constructed from one of the remaining productIds
                    const newFurniture = AspectFurniture.changeSelection(furniture, Article.fromProductId(productIds[0], articleId));
                    if (tenantId === undefined) {
                        return Effect.die('tenantId is undefined');
                    }
                    return Effect.succeed({
                        _tag: 'GenerateParticlesIntermediate',
                        tenantId,
                        errors: errors,
                        aspectFurniture: newFurniture,
                    });
                }
            })),
        });
        return particles.pipe(Effect.tap(Effect.logDebug), Effect.flatMap(state => {
            if (state._tag === 'GenerateParticlesSuccess') {
                const particleSlots = state.particles
                    .flatMap(Particle.slots)
                    .map(slot => slot.name);
                const configurationSlots = state.aspectFurniture.configurationSet.parts
                    .filter(p => !p.isParent)
                    .map(p => p.productType);
                const furniture = create({
                    position,
                    rotation,
                    instanceId,
                    configuration: Config.construct({
                        particles: state.particles,
                        heroParts: [],
                    }),
                });
                return Effect.succeed({
                    furniture,
                    articleErrors: state.errors,
                    slotMismatchError: particleSlots.length === configurationSlots.length
                        ? undefined
                        : new SlotMismatchError(configurationSlots.filter(slot => !particleSlots.includes(slot))),
                });
            }
            else if (state._tag === 'GenerateParticlesFailure') {
                return Effect.fail(new GenerationFailure(af, state.reasons));
            }
            else {
                return Effect.die('unreachable');
            }
        }));
    };
    return { fromAspectFurniture };
}));
export const LiveFixtuurFurnitureService = BaseFixtuurFurnitureLayer.pipe(Layer.provide(Particle.LiveFixtuurConfigurationParticleService));
export const fromAspectFurniture = (_, af) => Effect.andThen(FixtuurFurnitureService, s => s.fromAspectFurniture(af));
